import {type ConfiguracionDeVista, type CupoDeCashflow, type ResumenDeCashflow} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {descargarTablasEnExcel, useResumenDeCashflow} from '../api/cashflow';
import {AcuerdosDePago} from '../componentes/cashflow-acuerdos';
import {BarraDeVistas} from '../componentes/barra-de-vistas';
import {FiltrosDePeriodo, MovimientosDeCashflow, TablaDeMovimientos} from '../componentes/cashflow-movimientos';
import {DEFINICION_POR_EMPRESA} from '../componentes/columnas-cashflow';
import {ColumnasDeCashflow, TablaDeCashflowComun, type HojaParaExcel} from '../componentes/tabla-de-cashflow';
import {MenuDeHerramientas, OPCION_DE_MENU} from '../componentes/menu-de-herramientas';
import {CargaDePagos, PagosATerceros} from '../componentes/cashflow-pagos';
import {AYUDA_CUPO, AYUDA_FILTROS_CASHFLOW} from '../componentes/ayuda-cashflow';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {localeActual, tr, type Texto} from '../i18n/idioma';
import {fechaHora, mes, numero, usd} from '../utilidades/formato';

// Colores de las dos series (los mismos del comparativo del inicio): entra en verde, sale en dorado.
// Colores de la marca Sun Valley: lo que entra en verde "bien", lo que sale en oro.
const COLOR_ENTRA = '#2f6b3a';
const COLOR_SALE = '#c9963f';
const CASI_LLENO = 0.9;

const VISTAS = ['resumen', 'movimientos', 'pagos', 'acuerdos'] as const;
type Vista = (typeof VISTAS)[number];

// Cashflow (decision 0042): donde esta el dinero de las financiaciones, cuando
// recibe y paga cada empresa, y como quedan los cupos con los pagos a
// proveedores terceros que vienen.
export function PaginaCashflow() {
  return (
    <ColumnasDeCashflow>
      {(columnas, tablas) => <ContenidoDeCashflow columnas={columnas} tablas={tablas} />}
    </ColumnasDeCashflow>
  );
}

// Columnas visibles de cada tabla (las guarda y las cambia una vista).
type ColumnasDeCadaTabla = Parameters<Parameters<typeof ColumnasDeCashflow>[0]['children']>[0];

function ContenidoDeCashflow({columnas, tablas}: {columnas: ColumnasDeCadaTabla; tablas: Map<string, HojaParaExcel>}) {
  const [parametros, setParametros] = useSearchParams();
  const pedida = parametros.get('vista') as Vista | null;
  const vista: Vista = pedida && VISTAS.includes(pedida) ? pedida : 'resumen';
  const permisos = usePermisos();
  // Año y mes de zarpe: se conservan al cambiar de pestaña.
  const periodo: Record<string, string> = {};
  for (const clave of ['anioEtd', 'mesEtd']) {
    const v = parametros.get(clave);
    if (v) periodo[clave] = v;
  }
  const resumen = useResumenDeCashflow(new URLSearchParams(periodo).toString());
  const [cargandoPagos, setCargandoPagos] = useState(false);
  const [descarga, setDescarga] = useState<{texto: string; error: boolean} | null>(null);
  const irA = (v: Vista, extra: Record<string, string> = {}) =>
    setParametros(new URLSearchParams({...(v === 'resumen' ? {} : {vista: v}), ...periodo, ...extra}));
  const cambiarPeriodo = (cambios: {anioEtd?: string; mesEtd?: string}) => {
    const nuevos = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) nuevos.set(k, v);
      else nuevos.delete(k);
    }
    nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  // Una vista guarda la pestaña, los filtros y las columnas de cada tabla.
  const extraDeVista = Object.fromEntries(Object.entries(columnas).map(([tabla, [ids]]) => [tabla, ids.join(',')]));
  const aplicarVista = (configuracion: ConfiguracionDeVista) => {
    setParametros(new URLSearchParams(configuracion.parametros), {replace: true});
    for (const [tabla, valor] of Object.entries(configuracion.extra))
      if (typeof valor === 'string' && tabla in columnas)
        columnas[tabla as keyof ColumnasDeCadaTabla][1](valor.split(',').filter(Boolean));
  };
  // Una hoja por cada tabla que se ve en la pestaña, con sus columnas y los filtros puestos.
  const descargarLoQueSeVe = async () => {
    const hojas = [...tablas.values()];
    if (!hojas.length) {
      setDescarga({
        texto: tr('No hay tablas para descargar en esta vista.', 'No tables to download in this view.'),
        error: true,
      });
      return;
    }
    setDescarga({texto: tr('Armando el Excel…', 'Building the Excel…'), error: false});
    try {
      const nombre = pestanas.find(([v]) => v === vista)?.[1] ?? 'Cashflow';
      await descargarTablasEnExcel(
        `Cashflow - ${nombre} ${new Date().toISOString().slice(0, 10)}`,
        await Promise.all(
          hojas.map(async h => ({nombre: h.nombre, encabezados: h.encabezados, filas: await h.filas()})),
        ),
      );
      setDescarga(null);
    } catch (error) {
      setDescarga({
        texto: error instanceof Error ? error.message : tr('No se pudo descargar.', 'Could not download.'),
        error: true,
      });
    }
  };
  const pestanas: Array<[Vista, string]> = [
    ['resumen', tr('Resumen', 'Summary')],
    ['movimientos', tr('Movimientos', 'Money movements')],
    ['pagos', tr('Pagos a terceros', 'Third-party payments')],
    ['acuerdos', tr('Acuerdos', 'Agreements')],
  ];
  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">Cashflow</h1>
          <p className="max-w-3xl text-sm text-apagado">
            {tr(
              'Quién paga, quién recibe, cuánto y cuándo: los pagos de las empresas del grupo a proveedores terceros y el dinero de las financiaciones (desembolsos y pagos de los compradores), con lo que queda de cada cupo.',
              'Who pays, who receives, how much and when: payments from group companies to third-party suppliers and financing money (disbursements and buyer payments), with what is left of each credit line.',
            )}
            {resumen.data?.carga &&
              tr(
                ` Pagos a terceros de ${resumen.data.carga.archivo}, cargado el ${fechaHora(resumen.data.carga.fecha)}.`,
                ` Third-party payments from ${resumen.data.carga.archivo}, loaded on ${fechaHora(resumen.data.carga.fecha)}.`,
              )}
          </p>
        </div>
        {/* Herramientas (Daniel, 15/10/2026): cupos, carga del Payment Schedule y descarga de lo que se ve. */}
        <MenuDeHerramientas>
          {cerrar => (
            <>
              <button
                type="button"
                className={OPCION_DE_MENU}
                onClick={() => {
                  cerrar();
                  void descargarLoQueSeVe();
                }}
              >
                {tr('⬇ Descargar lo que se ve (Excel)', '⬇ Download what is shown (Excel)')}
              </button>
              {permisos.editar && (
                <button
                  type="button"
                  className={OPCION_DE_MENU}
                  onClick={() => {
                    cerrar();
                    setCargandoPagos(true);
                  }}
                >
                  {tr('⬆ Cargar Payment Schedule', '⬆ Load Payment Schedule')}
                </button>
              )}
              {permisos.editar && (
                <Link to="/configuracion-de-campos/financiadores" className={OPCION_DE_MENU} onClick={cerrar}>
                  {tr('⚙ Financiadores y cupos', '⚙ Financers and credit lines')}
                </Link>
              )}
            </>
          )}
        </MenuDeHerramientas>
      </div>
      {cargandoPagos && permisos.editar && <CargaDePagos onCerrar={() => setCargandoPagos(false)} />}
      {descarga && (
        <p role="status" className={`text-sm ${descarga.error ? 'text-alerta' : 'text-apagado'}`}>
          {descarga.texto}
        </p>
      )}
      <nav
        className="flex flex-wrap gap-1 border-b border-linea"
        aria-label={tr('Vistas de Cashflow', 'Cashflow views')}
      >
        {pestanas.map(([v, texto]) => (
          <button
            key={v}
            type="button"
            aria-current={vista === v ? 'page' : undefined}
            onClick={() => irA(v)}
            className={`-mb-px rounded-t-md border px-3 py-1.5 text-sm ${
              vista === v
                ? 'border-linea border-b-white bg-white font-medium text-verde-oscuro'
                : 'border-transparent text-apagado hover:text-tinta'
            }`}
          >
            {texto}
          </button>
        ))}
      </nav>
      <BarraDeVistas
        pantalla="cashflow"
        actual={{parametros: parametros.toString(), columnas: null, extra: extraDeVista}}
        onAplicar={aplicarVista}
      />
      {vista === 'resumen' && (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-linea bg-white px-3 py-2">
            <div className="grid w-full max-w-xl grid-cols-2 gap-2">
              <FiltrosDePeriodo parametros={parametros} opciones={resumen.data?.opciones} cambiar={cambiarPeriodo} />
            </div>
            <p className="pb-1 text-xs text-apagado">
              {tr(
                'Filtran por el zarpe de la exportación lo que cada empresa recibe y paga, el mes a mes y lo próximo. Los cupos son siempre el total.',
                'They filter by the export departure what each company receives and pays, the monthly view and what is next. Credit lines always show the total.',
              )}
            </p>
          </div>
          {resumen.isError && <p className="text-sm text-alerta">{resumen.error.message}</p>}
          {resumen.isPending && <p className="text-sm text-apagado">{tr('Calculando…', 'Calculating…')}</p>}
          {resumen.data && (
            <Resumen r={resumen.data} onEmpresa={e => irA('movimientos', {empresa: e, estado: 'previsto|vencido'})} />
          )}
        </>
      )}
      {vista === 'movimientos' && <MovimientosDeCashflow />}
      {vista === 'pagos' && <PagosATerceros />}
      {vista === 'acuerdos' && <AcuerdosDePago />}
    </section>
  );
}

function Resumen({r, onEmpresa}: {r: ResumenDeCashflow; onEmpresa: (empresa: string) => void}) {
  return (
    <div className="space-y-5" data-prueba="resumen-cashflow">
      <div>
        <h2 className="text-sm font-semibold tracking-wide text-apagado uppercase">
          {tr('Cupos y dónde está el dinero', 'Credit lines and where the money is')}
        </h2>
        <div className="mt-2 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {r.cupos.map(c => (
            <Cupo key={c.codigo} c={c} />
          ))}
        </div>
        {r.sinLineaUsd > 0 && (
          <p className="mt-2 rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-sm">
            {tr(
              `Hay ${usd(r.sinLineaUsd)} en pagos a terceros pendientes sin línea de financiación: no se descuentan de ningún cupo. Asígnales la línea en `,
              `There are ${usd(r.sinLineaUsd)} in pending third-party payments without a financing line: they are not deducted from any line. Assign their line in `,
            )}
            <Link to="/cashflow?vista=acuerdos" className="text-verde underline">
              {tr('Acuerdos', 'Agreements')}
            </Link>
            .
          </p>
        )}
      </div>
      <PorEmpresa r={r} onEmpresa={onEmpresa} />
      <GraficaPorMes r={r} />
      <Proximos r={r} />
    </div>
  );
}

function Fila({titulo, valor, fuerte, ayuda}: {titulo: string; valor: string; fuerte?: boolean; ayuda?: Texto}) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-apagado">
        {titulo}
        {ayuda && <NotaDeAyuda titulo={titulo} texto={ayuda} />}
      </dt>
      <dd className={`whitespace-nowrap tabular-nums ${fuerte ? 'font-medium text-tinta' : ''}`}>{valor}</dd>
    </div>
  );
}

// Tarjeta de un cupo, compacta (Daniel, 15/10/2026): lo usado y la barra en una
// sola franja, notas cortas y las cifras juntas.
function Cupo({c}: {c: CupoDeCashflow}) {
  const parte = c.cupoUsd && c.cupoUsd > 0 ? Math.min(c.usadoUsd / c.cupoUsd, 1) : 0;
  const lleno = parte >= CASI_LLENO;
  const negativo = c.disponibleDespuesUsd !== null && c.disponibleDespuesUsd < 0;
  // Un cupo compartido es una sola tarjeta: "BLQ + BLQM" (Daniel, 13/10/2026).
  const nombre = [c.nombre, ...c.incluye].join(' + ');
  const nota = c.comparteCupoCon
    ? tr(
        `Usa el cupo de ${c.comparteCupoCon}: lo suyo ya está sumado allá.`,
        `Uses the ${c.comparteCupoCon} line: its amount is already added there.`,
      )
    : c.cupoUsd === null
      ? tr('Cupo por definir.', 'Credit line not defined yet.')
      : c.incluye.length > 0
        ? tr(
            `Un solo cupo para ${[c.codigo, ...c.incluye].join(' y ')}: la suma de los dos.`,
            `One credit line for ${[c.codigo, ...c.incluye].join(' and ')}: the sum of both.`,
          )
        : null;
  return (
    <div className="rounded-lg border border-linea bg-white px-3 py-2.5" data-prueba={`cupo-cashflow-${c.codigo}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-medium text-verde-oscuro" title={nota ?? undefined}>
          {nombre}
        </div>
        {c.nombre !== c.codigo && <div className="text-xs text-apagado">{[c.codigo, ...c.incluye].join(' + ')}</div>}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-xl font-semibold tabular-nums">{usd(c.usadoUsd)}</span>
        <span className="text-xs text-apagado">
          {tr('usado hoy', 'used today')}
          <NotaDeAyuda titulo={tr('usado hoy', 'used today')} texto={AYUDA_CUPO.usado} />
          {c.cupoUsd !== null && !c.comparteCupoCon && ` · ${Math.round(parte * 100)} %`}
        </span>
      </div>
      {c.cupoUsd !== null && !c.comparteCupoCon && (
        <div
          className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-papel"
          role="meter"
          aria-valuenow={c.usadoUsd}
        >
          <div
            className={`h-full rounded-full ${lleno ? 'bg-alerta' : 'bg-verde'}`}
            style={{width: `${parte * 100}%`}}
          />
        </div>
      )}
      {nota && <p className="mt-1 text-xs leading-snug text-apagado">{nota}</p>}
      <dl className="mt-1.5 space-y-px text-xs">
        {c.cupoUsd !== null && (
          <Fila titulo={tr('Cupo', 'Credit line')} valor={usd(c.cupoUsd)} ayuda={AYUDA_CUPO.cupo} />
        )}
        {c.disponibleUsd !== null && (
          <Fila
            titulo={tr('Disponible hoy', 'Available today')}
            valor={usd(c.disponibleUsd)}
            ayuda={AYUDA_CUPO.disponible}
          />
        )}
        <Fila
          titulo={tr('Pagos a terceros por hacer', 'Third-party payments due')}
          valor={usd(c.compromisosUsd)}
          ayuda={AYUDA_CUPO.compromisos}
        />
        {c.disponibleDespuesUsd !== null && (
          <div className={`flex justify-between gap-2 ${negativo ? 'font-medium text-alerta' : ''}`}>
            <dt className={negativo ? '' : 'text-apagado'}>
              {negativo && '⚠ '}
              {tr('Disponible después de pagarlos', 'Available after paying them')}
              <NotaDeAyuda
                titulo={tr('Disponible después de pagarlos', 'Available after paying them')}
                texto={AYUDA_CUPO.disponibleDespues}
              />
            </dt>
            <dd className="whitespace-nowrap tabular-nums">{usd(c.disponibleDespuesUsd)}</dd>
          </div>
        )}
        <Fila
          titulo={tr('Desembolsos por recibir', 'Disbursements to receive')}
          valor={usd(c.desembolsosPendientesUsd)}
          ayuda={AYUDA_CUPO.desembolsos}
        />
        <Fila
          titulo={tr('Por cobrar a los compradores', 'Due from buyers')}
          valor={usd(c.porCobrarUsd)}
          ayuda={AYUDA_CUPO.porCobrar}
        />
      </dl>
      {c.porEmpresa.length > 0 && (
        <div className="mt-2 border-t border-linea/70 pt-1.5 text-xs">
          <div className="text-apagado">
            {tr('El dinero usado lo recibió', 'The money used went to')}
            <NotaDeAyuda
              titulo={tr('El dinero usado lo recibió', 'The money used went to')}
              texto={AYUDA_CUPO.porEmpresa}
            />
          </div>
          <dl className="mt-0.5 space-y-px">
            {c.porEmpresa.map(e => (
              <Fila key={e.empresa} titulo={e.empresa} valor={usd(e.usd)} fuerte />
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
function PorEmpresa({r, onEmpresa}: {r: ResumenDeCashflow; onEmpresa: (empresa: string) => void}) {
  if (!r.empresas.length) return null;
  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba="cashflow-por-empresa">
      <TablaDeCashflowComun
        tabla="porEmpresa"
        hoja={tr('Por empresa', 'By company')}
        definicion={DEFINICION_POR_EMPRESA}
        filas={r.empresas.map(e => ({...e, netoUsd: e.entradasUsd - e.salidasUsd}))}
        claveDeFila={e => e.empresa}
        altoMaximo="24rem"
        mensajeVacio={tr('No hay empresas con estos filtros.', 'No companies with these filters.')}
        titulo={
          <>
            <h2 className="font-medium">
              {tr('Lo que cada empresa tiene por recibir y por pagar', 'What each company has to receive and to pay')}
            </h2>
            <p className="text-xs text-apagado">
              {tr(
                'Movimientos previstos y vencidos (sin los ya hechos), en USD. Clic en una empresa para ver sus movimientos. Para que un código salga con el nombre de la empresa (NIM = Nimbus), agrégalo en Configuración de campos → Empresas.',
                'Expected and overdue movements (not the ones already done), in USD. Click a company to see its movements. To show a code with the company name (NIM = Nimbus), add it in Field settings → Companies.',
              )}
            </p>
          </>
        }
        celda={(columna, e) => {
          if (columna.id === 'empresa')
            return (
              <button type="button" onClick={() => onEmpresa(e.empresa)} className="text-verde hover:underline">
                {e.empresa}
              </button>
            );
          if (columna.id.startsWith('vencido'))
            return (
              <span className={(e as Record<string, unknown>)[columna.id] ? 'text-alerta' : 'text-apagado'}>
                {columna.valor(e)}
              </span>
            );
          if (columna.id === 'netoUsd')
            return (
              <span className={`font-medium ${e.netoUsd < 0 ? 'text-alerta' : 'text-verde-oscuro'}`}>
                {columna.valor(e)}
              </span>
            );
          return undefined;
        }}
      />
    </div>
  );
}

function GraficaPorMes({r}: {r: ResumenDeCashflow}) {
  const empresas = r.empresas.map(e => e.empresa);
  const [elegida, setElegida] = useState<string>('');
  const empresa = empresas.includes(elegida) ? elegida : (empresas[0] ?? '');
  if (!empresa) return null;
  const corto = new Intl.NumberFormat(localeActual(), {notation: 'compact', maximumFractionDigits: 1});
  const datos = r.meses.map(m => {
    const suyo = m.empresas.find(x => x.empresa === empresa);
    return {mes: mes(m.mes), entra: suyo?.entradasUsd ?? 0, sale: suyo?.salidasUsd ?? 0};
  });
  const formato = (v: unknown) => (typeof v === 'number' ? `USD ${numero(v)}` : '—');
  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba="cashflow-grafica">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-medium">{tr('Entradas y salidas por mes', 'Money in and out by month')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'Lo previsto de los próximos 6 meses; lo vencido o sin fecha cuenta en el mes actual.',
              'Expected for the next 6 months; overdue or undated counts in the current month.',
            )}
          </p>
        </div>
        <label className="text-xs text-apagado">
          {tr('Empresa', 'Company')}
          <NotaDeAyuda texto={AYUDA_FILTROS_CASHFLOW.empresaGrafica} dentroDeEtiqueta />
          <select
            value={empresa}
            onChange={e => setElegida(e.target.value)}
            className="ml-2 rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
          >
            {empresas.map(e => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-2 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={datos} margin={{top: 8, right: 16, left: 0, bottom: 0}} barGap={2}>
            <CartesianGrid stroke="var(--color-linea)" vertical={false} />
            <XAxis
              dataKey="mes"
              tick={{fontSize: 12, fill: 'var(--color-apagado)'}}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{fontSize: 12, fill: 'var(--color-apagado)'}}
              tickLine={false}
              axisLine={false}
              tickFormatter={v => (typeof v === 'number' ? corto.format(v) : '')}
              width={60}
            />
            <Tooltip
              formatter={formato}
              cursor={{fill: 'var(--color-papel)'}}
              contentStyle={{background: 'var(--color-white)', borderColor: 'var(--color-linea)'}}
            />
            <Legend />
            <Bar
              dataKey="entra"
              name={tr('Por recibir', 'To receive')}
              fill={COLOR_ENTRA}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
              isAnimationActive={false}
            />
            <Bar
              dataKey="sale"
              name={tr('Por pagar', 'To pay')}
              fill={COLOR_SALE}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Proximos({r}: {r: ResumenDeCashflow}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-linea bg-white p-4" data-prueba="cashflow-proximos">
      <h2 className="font-medium">{tr('Vencido y próximos 30 días', 'Overdue and next 30 days')}</h2>
      <div className="mt-2">
        <TablaDeMovimientos
          filas={r.proximos}
          hoja={tr('Vencido y próximos 30 días', 'Overdue and next 30 days')}
          vacia={tr(
            'Nada vencido ni por pagar o recibir en los próximos 30 días.',
            'Nothing overdue or due in the next 30 days.',
          )}
        />
      </div>
    </div>
  );
}
